import React, { useState } from 'react';
import { ChevronRight, ChevronUp } from 'lucide-react';
import LikeButtons from '../icon/LikeButton';
import UnLikeButtons from '../icon/unlikebutton';
import DeleteButtons from '../icon/deleteicon';
import { getAuthorDisplayName } from '../../utils/displayName';
import { resolveAvatarUrl, handleAvatarError } from '../../utils/profilePicUrl';
import { isMediaImage, isMediaVideo, getMediaMimeType } from '../../utils/mediaDetect';
import MentionInput from '../shared/MentionInput';
import CaptionAssist, { plainExcerpt } from '../shared/CaptionAssist';
import { canDeleteGroupReply } from '../../utils/groupRole';
import './groupReplyThread.css';

/**
 * Single reply (and nested children) in a group post thread.
 */
export default function GroupReplyItem({ reply, post, depth, ctx, replyToName }) {
  const {
    API_URL,
    user,
    userLiked,
    likes,
    expandedContent,
    setExpandedContent,
    showAllImages,
    setShowAllImages,
    activeReplyBox,
    setActiveReplyBox,
    nestedReplyText,
    setNestedReplyText,
    nestedReplyMedia,
    setNestedReplyMedia,
    handleLike,
    handleDeleteReply,
    handleNestedReplySubmit,
    formatDate,
    createLinkifiedText,
  } = ctx;

  const avatarSrc = resolveAvatarUrl(reply, API_URL);
  const hasChildren = Array.isArray(reply.children) && reply.children.length > 0;
  const isNested = Number(depth) > 0;
  const authorName = getAuthorDisplayName(reply);
  const [showChildren, setShowChildren] = useState(true);

  const renderMediaItem = (item, idx) => {
    const url = item?.url;
    const mime = item?.type || item?.media_type;
    if (!url) return null;
    if (isMediaImage(url, mime)) {
      return (
        <img
          key={idx}
          src={url}
          alt="reply media"
          style={{
            display: 'block',
            maxWidth: '100%',
            width: 'auto',
            height: 'auto',
            borderRadius: 8,
            marginTop: 8,
          }}
        />
      );
    }
    if (isMediaVideo(url, mime)) {
      return (
        <video
          key={idx}
          controls
          playsInline
          style={{
            display: 'block',
            maxWidth: '100%',
            width: '100%',
            borderRadius: 8,
            marginTop: 8,
          }}
        >
          <source src={url} type={getMediaMimeType(url, mime)} />
        </video>
      );
    }
    return null;
  };

  return (
    <div
      className={`group-reply-thread${isNested ? ' is-nested' : ''}`}
      style={{ marginTop: depth === 0 ? 10 : 0 }}
    >
      <div className={`group-reply-card${isNested ? ' group-reply-card--child' : ''}`}>
        <div className="group-reply-row">
          <img
            className="group-reply-avatar"
            src={avatarSrc}
            alt=""
            onError={handleAvatarError}
          />

          <div className="group-reply-body">
            <div style={{ marginBottom: 2 }}>
              <div className="group-reply-name">{authorName}</div>
              <div className="group-reply-time">{formatDate(reply.created_at)}</div>
              {isNested && replyToName ? (
                <div className="group-reply-to">Replying to {replyToName}</div>
              ) : null}
            </div>

            <p
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
              dangerouslySetInnerHTML={{
                __html:
                  expandedContent[reply.id] || (reply.content || '').split(' ').length <= 50
                    ? createLinkifiedText(String(reply.content || ''))
                    : createLinkifiedText(
                        String(reply.content || '').split(' ').slice(0, 50).join(' ') + '...'
                      ),
              }}
            />

            {(reply.content || '').split(' ').length > 50 && (
              <button
                type="button"
                onClick={() =>
                  setExpandedContent((prev) => ({
                    ...prev,
                    [reply.id]: !prev[reply.id],
                  }))
                }
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#2f8fd6',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  padding: 0,
                  marginBottom: 8,
                }}
              >
                {expandedContent[reply.id] ? 'See less' : 'See more'}
              </button>
            )}

            {Array.isArray(reply.media) && reply.media.length > 0 && (
              <div style={{ marginBottom: 8, maxWidth: '100%', overflow: 'hidden' }}>
                {reply.media
                  .slice(0, showAllImages[reply.id] ? reply.media.length : 2)
                  .map((item, idx) => renderMediaItem(item, idx))}
                {reply.media.length > 2 && (
                  <button
                    type="button"
                    onClick={() =>
                      setShowAllImages((prev) => ({
                        ...prev,
                        [reply.id]: !prev[reply.id],
                      }))
                    }
                    style={{
                      border: 'none',
                      background: '#f4f8fb',
                      borderRadius: 8,
                      padding: '8px 12px',
                      color: '#2f8fd6',
                      marginTop: 8,
                      cursor: 'pointer',
                      fontWeight: 600,
                    }}
                  >
                    {showAllImages[reply.id] ? 'Show less' : 'See more media'}
                  </button>
                )}
              </div>
            )}

            <div className="group-reply-actions">
              <button
                type="button"
                className={`group-reply-like${
                  userLiked[`reply_${reply.id}`] ? ' is-active' : ''
                }`}
                onClick={() => handleLike(reply.id, post.group_id, 'reply')}
              >
                {userLiked[`reply_${reply.id}`] ? <LikeButtons /> : <UnLikeButtons />}
                <span>{likes[`reply_${reply.id}`] || 0}</span>
              </button>

              {user &&
                canDeleteGroupReply(
                  user,
                  reply,
                  ctx.members,
                  ctx.groupCreatedBy
                ) && (
                  <button
                    type="button"
                    onClick={() => handleDeleteReply(reply.id, post.group_id)}
                    style={{
                      fontSize: 13,
                      color: '#d9534f',
                      background: 'transparent',
                      border: 'none',
                      padding: '4px 6px',
                      cursor: 'pointer',
                    }}
                  >
                    <DeleteButtons />
                  </button>
                )}

              {user && (
                <button
                  type="button"
                  className="group-reply-reply-btn"
                  onClick={() =>
                    setActiveReplyBox((prev) => ({
                      ...prev,
                      [reply.id]: !prev[reply.id],
                    }))
                  }
                >
                  Reply
                </button>
              )}
            </div>

            {activeReplyBox[reply.id] && user && (
              <div
                style={{
                  marginTop: 10,
                  padding: 12,
                  background: '#f8fafc',
                  borderRadius: 12,
                  border: '1px solid #e5e7eb',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <MentionInput
                  userId={user?.id}
                  rows={2}
                  placeholder="Write a reply... Type @ to mention a connection"
                  value={nestedReplyText[reply.id] || ''}
                  onChange={(value) =>
                    setNestedReplyText((prev) => ({
                      ...prev,
                      [reply.id]: value,
                    }))
                  }
                  style={{
                    borderRadius: 8,
                    border: '1px solid #ddd',
                    padding: 8,
                    fontSize: 13,
                  }}
                />
                <CaptionAssist
                  kind="reply"
                  text={nestedReplyText[reply.id] || ''}
                  context={plainExcerpt(
                    `Replying to: ${reply.content || reply.text || ''}\nPost: ${post?.content || ''}`,
                    700
                  )}
                  userId={user?.id || user?.user_id}
                  style={{ marginTop: 8 }}
                  onApply={(next) =>
                    setNestedReplyText((prev) => ({
                      ...prev,
                      [reply.id]: next,
                    }))
                  }
                />

                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const files = Array.from(e.dataTransfer.files);
                    setNestedReplyMedia((prev) => ({
                      ...prev,
                      [reply.id]: [...(prev[reply.id] || []), ...files],
                    }));
                  }}
                  onClick={() => document.getElementById(`nested-upload-${reply.id}`)?.click()}
                  style={{
                    marginTop: 6,
                    padding: 10,
                    border: '2px dashed #9ec8e8',
                    borderRadius: 8,
                    textAlign: 'center',
                    cursor: 'pointer',
                    color: '#2f8fd6',
                    fontSize: 13,
                    background: '#f3f9fd',
                  }}
                >
                  Drag & drop or click to attach
                  <input
                    type="file"
                    id={`nested-upload-${reply.id}`}
                    multiple
                    accept="image/*,video/*"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setNestedReplyMedia((prev) => ({
                        ...prev,
                        [reply.id]: [...(prev[reply.id] || []), ...files],
                      }));
                    }}
                  />
                </div>

                {(nestedReplyMedia[reply.id] || []).length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                    {(nestedReplyMedia[reply.id] || []).map((file, idx) => (
                      <div key={idx} style={{ position: 'relative' }}>
                        {file.type.startsWith('image/') && (
                          <img
                            src={URL.createObjectURL(file)}
                            alt=""
                            style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6 }}
                          />
                        )}
                        <button
                          type="button"
                          onClick={() =>
                            setNestedReplyMedia((prev) => ({
                              ...prev,
                              [reply.id]: prev[reply.id].filter((_, i) => i !== idx),
                            }))
                          }
                          style={{
                            position: 'absolute',
                            top: -4,
                            right: -4,
                            background: '#d9534f',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '50%',
                            width: 18,
                            height: 18,
                            cursor: 'pointer',
                            fontSize: 11,
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => handleNestedReplySubmit(post.id, reply.id, post.group_id)}
                  style={{
                    marginTop: 8,
                    padding: '8px 16px',
                    background: '#2f8fd6',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 999,
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  Send
                </button>
              </div>
            )}
          </div>
        </div>

        {hasChildren && (
          <div className="group-reply-children">
            <button
              type="button"
              className="group-reply-toggle"
              onClick={() => setShowChildren((open) => !open)}
            >
              <span className="group-reply-toggle-icon" aria-hidden="true">
                {showChildren ? (
                  <ChevronUp size={14} strokeWidth={2.5} />
                ) : (
                  <ChevronRight size={14} strokeWidth={2.5} />
                )}
              </span>
              <span>
                {showChildren
                  ? 'Hide replies'
                  : `View ${reply.children.length} repl${
                      reply.children.length > 1 ? 'ies' : 'y'
                    }`}
              </span>
            </button>
            {showChildren &&
              reply.children.map((child) => (
                <GroupReplyItem
                  key={child.id}
                  reply={child}
                  post={post}
                  depth={depth + 1}
                  ctx={ctx}
                  replyToName={authorName}
                />
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
